import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

interface OverlayContentProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  closeLabel?: string;
  /** Where focus goes when the overlay closes (default: the element Radix remembers). Preventing the event and focusing the opener is how a dialog opened from a menu item hands focus back. */
  onCloseAutoFocus?: (event: Event) => void;
  /** Where focus goes when the overlay opens (default: the first control). A confirmation that stops something puts it on Cancel. */
  onOpenAutoFocus?: (event: Event) => void;
}

interface OverlayPanelProps extends Pick<OverlayContentProps, "className" | "onCloseAutoFocus" | "onOpenAutoFocus"> {
  hasDescription: boolean;
  children: ReactNode;
}

/**
 * The Radix content, remembering what had focus when the overlay opened. Radix hands focus back to its own `Trigger`; an
 * overlay opened from state (a Preview drawer, a confirmation) has none and would drop focus on the page body, so the opener
 * gets it back instead, unless the caller says where focus goes (a dialog opened from a menu item).
 */
function OverlayPanel({ className, hasDescription, onCloseAutoFocus, onOpenAutoFocus, children }: OverlayPanelProps) {
  const [opener] = useState(() => document.activeElement);
  const restoreFocus = (event: Event) => {
    if (onCloseAutoFocus) {
      onCloseAutoFocus(event);
      return;
    }
    if (opener instanceof HTMLElement && opener.isConnected && opener !== document.body) {
      event.preventDefault();
      opener.focus();
    }
  };
  return (
    <DialogPrimitive.Content className={className} onCloseAutoFocus={restoreFocus} onOpenAutoFocus={onOpenAutoFocus} {...(hasDescription ? {} : { "aria-describedby": undefined })}>
      {children}
    </DialogPrimitive.Content>
  );
}

function DialogHeader({ title, description, closeLabel }: Pick<OverlayContentProps, "title" | "description" | "closeLabel">) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
      <div className="min-w-0">
        <DialogPrimitive.Title className="text-base font-bold text-fg">{title}</DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="mt-1 text-sm text-fg-muted">{description}</DialogPrimitive.Description>
        ) : null}
      </div>
      <DialogPrimitive.Close asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={closeLabel ?? "Close"} className="shrink-0 pointer-coarse:h-11 pointer-coarse:w-11">
          <Icon name="x" />
        </Button>
      </DialogPrimitive.Close>
    </div>
  );
}

/**
 * `.modal-backdrop > .modal` (Radix: content nested in the overlay). The modal never grows past the
 * viewport: the header and footer stay put and the body scrolls, so a long form keeps its actions
 * reachable. Focus is trapped and restored by Radix; Esc and outside click close.
 */
export function DialogContent({ title, description, children, footer, className, closeLabel, onCloseAutoFocus, onOpenAutoFocus }: OverlayContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="modal-backdrop">
        <OverlayPanel className={cn("modal flex max-h-full flex-col outline-none", className)} hasDescription={Boolean(description)} onCloseAutoFocus={onCloseAutoFocus} onOpenAutoFocus={onOpenAutoFocus}>
          <DialogHeader title={title} description={description} closeLabel={closeLabel} />
          {children ? <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div> : null}
          {footer ? <div className="flex shrink-0 justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
        </OverlayPanel>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

/** Right-hand `.drawer-backdrop > .drawer`. */
export function SheetContent({ title, description, children, footer, className, closeLabel, onCloseAutoFocus }: OverlayContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="drawer-backdrop">
        <OverlayPanel className={cn("drawer outline-none", className)} hasDescription={Boolean(description)} onCloseAutoFocus={onCloseAutoFocus}>
          <DialogHeader title={title} description={description} closeLabel={closeLabel} />
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? <div className="flex shrink-0 justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
        </OverlayPanel>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}
